/**
 * State readers for MapVisual: what to geocode, and whether a center is
 * already known in state (skips the geocode round trip entirely). Same rule
 * as everywhere else in web/ — read what's there, never fabricate.
 */
import type { Advertiser, Geography } from '@shared/types';

/**
 * Strips descriptive parenthetical asides ("(main campus)") from a label,
 * collapsing whitespace and trailing punctuation left behind — mirrors the
 * server's own simplification in src/server/map.ts so a label that would
 * need a geocode retry doesn't even carry the parenthetical client-side.
 */
function stripParentheticals(label: string): string {
  const withoutParens = label.replace(/\s*\([^)]*\)/g, ' ');
  const collapsed = withoutParens.replace(/\s+/g, ' ').trim();
  return collapsed.replace(/[,;:.\s]+$/, '');
}

/** Best human place label to geocode, most specific first. */
export function geoQueryLabel(advertiser: Advertiser): string | null {
  const loc = advertiser.locations[0];
  if (loc?.address) return loc.address;
  if (loc?.city && loc?.state) return `${loc.city}, ${loc.state}`;
  if (loc?.city) return loc.city;
  if (loc?.label) return stripParentheticals(loc.label) || loc.label;
  return advertiser.name;
}

/** Coordinates already present in state, if any. */
export function geoExplicitCenter(
  advertiser: Advertiser,
  geography: Geography,
): { lat: number; lng: number } | undefined {
  const loc = advertiser.locations[0];
  if (typeof loc?.lat === 'number' && typeof loc?.lng === 'number') {
    return { lat: loc.lat, lng: loc.lng };
  }
  const center = geography.areas[0]?.center;
  if (center) return center;
  return undefined;
}
